import { SUPPLIER_BACKEND_API_ENDPOINTS } from "../config/apiConfig";
import { apiClient } from "./api-client.service";
import type { ApiResponse } from "./api-client.service";

import type {
  City,
  CityFilter,
  CityApiResponse,
  CityPayload,
} from "../types/cities.types";

const endpoints = SUPPLIER_BACKEND_API_ENDPOINTS.cities;

const failure = (error: unknown, fallback: string) => ({
  success: false,
  statusCode: 500,
  message: error instanceof Error ? error.message : fallback,
  resData: null,
});

export const citiesService = {
  async list(filter: CityFilter): Promise<CityApiResponse> {
    try {
      const params = new URLSearchParams();
      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.set(key, String(value));
        }
      });

      return await apiClient<CityApiResponse>(endpoints.list(params.toString()), {
        method: "GET",
        isAuthenticated: true,
        cache: "no-store",
      });
    } catch (error) {
      return failure(error, "Failed to load city list.") as unknown as CityApiResponse;
    }
  },

  async details(id: number | string): Promise<ApiResponse<City>> {
    try {
      return await apiClient<ApiResponse<City>>(endpoints.details(id), {
        method: "GET",
        isAuthenticated: true,
        cache: "no-store",
      });
    } catch (error) {
      return failure(error, "Failed to load city details.") as unknown as ApiResponse<City>;
    }
  },

  async create(payload: CityPayload): Promise<ApiResponse<City>> {
    try {
      return await apiClient<ApiResponse<City>>(endpoints.create, {
        method: "POST",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(error, "Failed to create city.") as unknown as ApiResponse<City>;
    }
  },

  async update(id: number | string, payload: Partial<CityPayload>): Promise<ApiResponse<City>> {
    try {
      return await apiClient<ApiResponse<City>>(endpoints.update(id), {
        method: "PATCH",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(error, "Failed to update city.") as unknown as ApiResponse<City>;
    }
  },

  async delete(id: number | string): Promise<ApiResponse<unknown>> {
    try {
      return await apiClient<ApiResponse<unknown>>(endpoints.delete(id), {
        method: "DELETE",
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(error, "Failed to delete city.") as unknown as ApiResponse<unknown>;
    }
  },
};
